import React, {useLayoutEffect, useRef, useState} from "react";
import {T} from "@tolgee/react";
import {
  Step,
  StepButton,
  StepConnector,
  Stepper,
  Typography,
  useTheme,
} from "@material-ui/core";
import FlexBox from "view/components/Layout/FlexBox";
import type {StepOrder} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList";
import Button from "@material-ui/core/Button";
import CancelIcon from "@material-ui/icons/Cancel";
import {green, grey, red} from "@material-ui/core/colors";
import clsx from "clsx";
import {
  CheckCircleOutline,
  ChevronLeft,
  ChevronRight,
} from "@material-ui/icons";
import {IconButton} from "@mui/material";
import {useCurrentValue} from "hooks/useCurrentValue";
import useStyles from "./styles";

type OrderStepperProps = {
  activeStep: number;
  steps: StepOrder[];
  handleStepChange: (idx: number) => void;
  isDisabled: boolean;
};

const STEP_WIDTH = 144;

const OrdersStepper = ({
  activeStep,
  steps,
  handleStepChange,
  isDisabled,
}: OrderStepperProps) => {
  const classes = useStyles();
  const {palette} = useTheme();

  const [stepsPerView, setStepsPerView] = useState<number>(0);
  const [startStep, setStartStep] = useState<number>(0);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const stepsPerViewRef = useCurrentValue(stepsPerView);

  const stepsSize = steps.length;

  useLayoutEffect(() => {
    const handleResize = () => {
      const rootEl = rootRef.current;
      if (!rootEl) {
        return;
      }

      const rootWidth = rootEl.clientWidth;
      const combinedStepsWidth = stepsSize * STEP_WIDTH;

      const newStepsPerView = Math.floor(
        Math.min(rootWidth, combinedStepsWidth) / STEP_WIDTH
      );

      const currentStepsPerView = stepsPerViewRef.current;
      const diff = newStepsPerView - currentStepsPerView;

      setStepsPerView(newStepsPerView);
      setStartStep((prevStartStep) => {
        let newStartStep = prevStartStep;
        if (diff > 0) {
          newStartStep = Math.max(0, prevStartStep - diff);
        }

        return newStartStep;
      });
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [stepsSize]);

  const visibleSteps = steps.slice(startStep, startStep + stepsPerView);

  const increaseStartStep = (byValue = 1) =>
    setStartStep((prev) => prev + byValue);

  const shouldShowRightArrow = startStep + stepsPerView < stepsSize;
  return (
    <div style={{width: "100%"}} ref={rootRef}>
      <FlexBox fullWidth alignCenter>
        {startStep > 0 && (
          <IconButton onClick={() => increaseStartStep(-1)}>
            <ChevronLeft />
          </IconButton>
        )}
        <Stepper
          nonLinear
          activeStep={activeStep}
          connector={<StepConnector className={classes.connector} />}
          classes={{root: classes.root}}
        >
          {visibleSteps.map((step, idx) => {
            const actualIdx = startStep + idx;
            const orderStep = steps[actualIdx];
            const textColor =
              actualIdx === activeStep
                ? palette.primary.main
                : orderStep.isPaid || orderStep.error
                ? grey[800]
                : grey[500];

            return (
              <Step key={step.id}>
                <StepButton
                  component={() => (
                    <Button
                      className={clsx(classes.button, {
                        [classes.activeButton]: actualIdx === activeStep,
                      })}
                      variant="outlined"
                      color="primary"
                      startIcon={<StepIcon stepOrder={steps[actualIdx]} />}
                      onClick={() => handleStepChange(actualIdx)}
                      disabled={isDisabled}
                    >
                      <Typography variant="h3" style={{color: textColor}}>
                        <T
                          keyName="exhibitor.cart.payment.order_step"
                          params={{number: actualIdx + 1}}
                        />
                      </Typography>
                    </Button>
                  )}
                />
              </Step>
            );
          })}
        </Stepper>
        {shouldShowRightArrow && (
          <IconButton onClick={() => increaseStartStep()}>
            <ChevronRight />
          </IconButton>
        )}
      </FlexBox>
    </div>
  );
};

const StepIcon = ({stepOrder}: {stepOrder: StepOrder}) => {
  const {error, isPaid} = stepOrder;

  if (error) {
    return <CancelIcon htmlColor={red[500]} />;
  }
  if (isPaid) {
    return (
      <CheckCircleOutline
        htmlColor={green[500]}
        style={{backgroundColor: green[50], borderRadius: "150%"}}
      />
    );
  }

  return null;
};
export default OrdersStepper;
